"use client"

import { type ReactNode, Suspense } from "react"
import { Header } from "./header"
import { SidebarContent } from "./sidebar" 
import { LoadingSpinner } from "./loading-spinner"
import { OnboardingTour } from "@/components/onboarding/onboarding-tour"
import { NotificationPermissionPrompt } from "@/components/shared/notification-permission-prompt"
import { WhatsNewModal } from "@/components/shared/whats-new-modal"
import { CommandPalette } from "@/components/shared/command-palette"
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts"
import { useHasMounted } from "@/hooks/use-has-mounted"
import { useProfileStore } from "@/stores/profile-store"
import { useMutationBlockerStore } from "@/stores/mutation-blocker-store"
import { useLayoutStore } from "@/stores/layout-store"
import { isRailWidth } from "@/lib/layout-preferences"
import { SidebarResizer } from "./sidebar-resizer"
import { MutationBlockingOverlay } from "./mutation-blocking-overlay"

interface AppLayoutProps {
  children: ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const hasSeenOnboarding = useProfileStore((s) => s.profile?.has_seen_onboarding === true)
  const hasSeenNotificationPrompt = useProfileStore((s) => s.profile?.has_seen_notification_prompt === true)
  const activeMutation = useMutationBlockerStore((state) => state.activeMutation)
  const sidebarWidth = useLayoutStore((state) => state.sidebarWidth)
  const hasMounted = useHasMounted()

  // The rail is a width, not a mode: nothing stores it and nothing else asks.
  const isRail = isRailWidth(sidebarWidth)

  useKeyboardShortcuts()

  return (
    <div className="flex h-screen flex-col overflow-hidden" aria-busy={activeMutation !== null}>
      {/* Fixed Header */}
      <div className="flex-shrink-0">
        <Header />
        {hasMounted && hasSeenOnboarding && (
          <>
            <NotificationPermissionPrompt />
            {hasSeenNotificationPrompt && <WhatsNewModal />}
          </>
        )}
      </div>

      {/* Main layout with fixed sidebar and scrollable content */}
      <div className="flex flex-1 overflow-hidden">
        {/* The width is whatever the user last dragged it to, held in a CSS
            variable so a drag costs one style write per frame instead of a
            React render. Collapsing narrows it to the icon rail rather than
            removing it: a sidebar that disappears strands a lone button beside
            the logo and shoves the page against the window edge.

            No width transition while dragging: animating a value that is
            already following the pointer makes the edge lag behind the cursor. */}
        {/* The scroll lives on the inner element, not the aside.

            `overflow-y` on the aside forces `overflow-x` to compute as auto
            too, which clips anything crossing its right edge — and both the
            seam and its collapse chevron are deliberately centred on that
            edge. They would have been cut in half, or produced a horizontal
            scrollbar in the sidebar. */}
        <aside className="group/sidebar relative hidden w-[var(--sidebar-w)] flex-shrink-0 border-r border-border/40 bg-sidebar md:block">
          <div className="h-full overflow-y-auto">
            <Suspense fallback={<LoadingSpinner />}>
              <SidebarContent idPrefix="desktop-sidebar" collapsed={isRail} showCollapse />
            </Suspense>
          </div>
          <SidebarResizer />
        </aside>

        {/* Scrollable Main Content */}
        <main className="flex-1 overflow-y-auto">
          <Suspense fallback={<LoadingSpinner />}>
            {children}
          </Suspense>
          <OnboardingTour />
        </main>
      </div>

      <CommandPalette />
      <MutationBlockingOverlay />
    </div>
  )
}
